import {ErrorCode} from '@sollapay/enums';
import {
  queryOptions,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';

import {useApiClient} from '@/hooks';
import {getErrorCode} from '@/utils';

import {documentsApi} from '../api';

import type {DocumentContext, DocumentEntityType, DocumentType} from '@sollapay/enums';
import type {HttpClient} from '@sollapay/http-client';
import type {
  DocumentDto,
  DocumentMetadata,
  DocumentProcessingStatusDto,
  PresignDocumentDto
} from '@sollapay/types';

// ─── Query Keys ────────────────────────────────────────────────────────────────

export const documentKeys = {
  all: (entityType: DocumentEntityType, entityId: string) =>
    ['documents', entityType, entityId] as const,
  lists: (entityType: DocumentEntityType, entityId: string) =>
    [...documentKeys.all(entityType, entityId), 'list'] as const,
  list: (entityType: DocumentEntityType, entityId: string, documentContext: DocumentContext) =>
    [...documentKeys.lists(entityType, entityId), documentContext] as const
};

export const documentProcessingKeys = {
  all: ['document-processing'] as const,
  byDocument: (documentId: string) => [...documentProcessingKeys.all, documentId] as const
};

// ─── Document CRUD Hooks ────────────────────────────────────────────────────────

type DocumentListData = {documents: DocumentDto[]};

function documentsListQueryOptions(
  client: HttpClient,
  entityType: DocumentEntityType,
  entityId: string,
  documentContext: DocumentContext
) {
  return queryOptions({
    queryKey: documentKeys.list(entityType, entityId, documentContext),
    queryFn: () =>
      documentsApi
        .list<DocumentListData>(client, entityType, entityId, documentContext)
        .then(res => res.data.documents),
    enabled: !!entityId
  });
}

export function useDocumentsQuery(
  entityType: DocumentEntityType,
  entityId: string,
  documentContext: DocumentContext
) {
  const client = useApiClient();
  return useQuery(documentsListQueryOptions(client, entityType, entityId, documentContext));
}

/** One list per document context; `updatedAtKey` changes whenever any of them refetches. */
export function useDocumentsQueriesByContext(
  entityType: DocumentEntityType,
  entityId: string,
  documentContexts: DocumentContext[]
): {documentsByContext: Map<DocumentContext, DocumentDto[] | undefined>; updatedAtKey: string} {
  const client = useApiClient();

  const results = useQueries({
    queries: documentContexts.map(documentContext =>
      documentsListQueryOptions(client, entityType, entityId, documentContext)
    )
  });

  return {
    documentsByContext: new Map(
      documentContexts.map((context, index) => [context, results[index]?.data])
    ),
    updatedAtKey: results.map(result => result.dataUpdatedAt).join('|')
  };
}

type DocumentDownloadData = {download: {url: string}};

export function useDownloadDocumentMutation(
  entityType: DocumentEntityType,
  entityId: string,
  documentContext: DocumentContext
) {
  const client = useApiClient();

  return useMutation({
    mutationFn: async (documentId: string) => {
      const res = await documentsApi.download<DocumentDownloadData>(
        client,
        entityType,
        entityId,
        documentContext,
        documentId
      );
      return res.data.download.url;
    }
  });
}

export function useViewDocumentMutation(
  entityType: DocumentEntityType,
  entityId: string,
  documentContext: DocumentContext
) {
  const client = useApiClient();

  return useMutation({
    mutationFn: async (documentId: string) => {
      const res = await documentsApi.download<DocumentDownloadData>(
        client,
        entityType,
        entityId,
        documentContext,
        documentId,
        true // inline
      );
      return res.data.download.url;
    }
  });
}

type PresignData = {document: PresignDocumentDto};

export function useUploadDocumentMutation(
  entityType: DocumentEntityType,
  entityId: string,
  documentContext: DocumentContext
) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      documentType,
      metadata,
      skipProcessing,
      onProgress
    }: {
      file: File;
      documentType: DocumentType;
      metadata?: DocumentMetadata;
      skipProcessing?: boolean;
      onProgress?: (percent: number) => void;
    }) => {
      const presignRes = await documentsApi.presign<PresignData>(
        client,
        entityType,
        entityId,
        documentContext,
        {
          documentType,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
          metadata
        }
      );

      await documentsApi.uploadToS3(presignRes.data.document.uploadUrl, file, onProgress);

      // Retry complete up to 2 extra times on transient failures. If the server
      // already processed it (DOCUMENT_ALREADY_UPLOADED), the upload succeeded
      // despite the client seeing an error on a prior attempt.
      let completeError: unknown;
      for (let attempt = 0; attempt <= 2; attempt++) {
        try {
          await documentsApi.complete(client, entityType, entityId, documentContext, {
            documentId: presignRes.data.document.documentId,
            skipProcessing
          });
          completeError = undefined;
          break;
        } catch (err) {
          if (getErrorCode(err) === ErrorCode.DOCUMENT_ALREADY_UPLOADED) {
            completeError = undefined;
            break;
          }
          if (getErrorCode(err) === ErrorCode.DOCUMENT_CONTENT_REJECTED) throw err;
          completeError = err;
          if (attempt < 2) await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
        }
      }
      if (completeError) throw completeError;

      return presignRes.data.document.documentId;
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: documentKeys.list(entityType, entityId, documentContext)
      });
    }
  });
}

export function useDeleteDocumentMutation(
  entityType: DocumentEntityType,
  entityId: string,
  documentContext: DocumentContext
) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) =>
      documentsApi.delete(client, entityType, entityId, documentContext, documentId),
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: documentKeys.list(entityType, entityId, documentContext)
      });
    }
  });
}

// ─── Processing Status Hooks ────────────────────────────────────────────────────

/**
 * Fetches processing status for multiple documents. Pass undefined to skip individual entries.
 * `enabled` stops refetching (e.g. after the user manually stops AI generation) — the query stays
 * disabled even when a realtime event would otherwise invalidate and refetch it. Pass an array to
 * disable per index; a disabled entry keeps its document id, so its cached status survives and
 * resuming reads from cache instead of refetching.
 */
export function useDocumentsProcessingStatuses(
  documentIds: Array<string | undefined>,
  {enabled = true}: {enabled?: boolean | readonly boolean[]} = {}
) {
  const client = useApiClient();

  return useQueries({
    queries: documentIds.map((id, index) => ({
      queryKey: documentProcessingKeys.byDocument(id ?? `__disabled_${index}`),
      queryFn: async (): Promise<DocumentProcessingStatusDto> => {
        const res = await documentsApi.getProcessingStatus(client, id as string);
        return res.data;
      },
      enabled: !!id && (Array.isArray(enabled) ? (enabled[index] ?? true) : enabled),
      placeholderData: (prev: DocumentProcessingStatusDto | undefined) => prev
    }))
  });
}
